import AsyncStorage from '@react-native-async-storage/async-storage';
import type { QueryClient } from '@tanstack/react-query';
import { DEEP_LINK_TYPES } from '../types/deepLink.types';
import { DEEP_LINK_REFETCH_KEYS } from '../constants/deepLink.constants';
import type { DeepLinkType, PendingDeepLink, AxiosLikeError } from '../types/deepLink.types';

const PENDING_DEEP_LINK_KEY = '@zporti/pending_deep_link';

const VALID_TYPES: readonly string[] = Object.values(DEEP_LINK_TYPES);

export function isValidDeepLinkType(type: string): type is DeepLinkType {
  return VALID_TYPES.includes(type);
}

export async function savePendingDeepLink(
  type: DeepLinkType,
  token: string
): Promise<void> {
  const payload: PendingDeepLink = { type, token, savedAt: Date.now() };
  await AsyncStorage.setItem(PENDING_DEEP_LINK_KEY, JSON.stringify(payload));
}

export async function getPendingDeepLink(): Promise<PendingDeepLink | null> {
  const raw = await AsyncStorage.getItem(PENDING_DEEP_LINK_KEY);
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as unknown;
    if (
      parsed &&
      typeof parsed === 'object' &&
      'type' in parsed &&
      'token' in parsed &&
      'savedAt' in parsed
    ) {
      return parsed as PendingDeepLink;
    }
  } catch {
    // Corrupted data, clear it
    await AsyncStorage.removeItem(PENDING_DEEP_LINK_KEY);
  }
  return null;
}

export async function clearPendingDeepLink(): Promise<void> {
  await AsyncStorage.removeItem(PENDING_DEEP_LINK_KEY);
}

/**
 * Invalidate and refetch all query keys affected by a deep link accept.
 * Invalidation ensures any active consumers see data as stale; refetch populates cache.
 * Awaits all refetches so the cache is fresh before navigation.
 */
export async function refetchDeepLinkQueries(queryClient: QueryClient): Promise<void> {
  for (const key of DEEP_LINK_REFETCH_KEYS) {
    queryClient.invalidateQueries({ queryKey: [...key] });
  }
  await Promise.all(
    DEEP_LINK_REFETCH_KEYS.map((key) =>
      queryClient.refetchQueries({ queryKey: [...key] }),
    ),
  );
  // Yield so the cache update is committed before the caller navigates
  await new Promise((r) => setTimeout(r, 0));
}

/**
 * Extract a user-facing error message from an axios-like error.
 * Prefers `data.error` (most Zporti JSON APIs), then `data.message` (e.g. express-rate-limit).
 */
export function getApiErrorMessage(err: unknown, fallback: string): string {
  const axiosErr = err as AxiosLikeError;
  const data = axiosErr?.response?.data;
  if (!data || typeof data !== 'object') {
    return fallback;
  }
  const errField = (data as { error?: unknown }).error;
  if (typeof errField === 'string' && errField.trim().length > 0) {
    return errField.trim();
  }
  const msgField = (data as { message?: unknown }).message;
  if (typeof msgField === 'string' && msgField.trim().length > 0) {
    return msgField.trim();
  }
  return fallback;
}
